import { STORAGE_KEY_DEVICE_PARAM_SCANNER_DELAY } from '@/utils/storage';

let Scanner_Delay = localStorage.getItem(STORAGE_KEY_DEVICE_PARAM_SCANNER_DELAY) || 100;

export const updateScannerDelay = (delay: number) => {
  Scanner_Delay = delay;
};

let listenerId = 0;

// export const SCAN_TYPES = {
//   User: 'user',
//   Goods: 'goods',
//   GoodsWithNum: 'goodsWithNum',
//   Order: 'order',
//   Card: 'card',
//   PayAuthCode: 'payAuthCode',
//   All: 'all',
//   Test: 'test',
//   ActionUserBind: 'actionUserBind',
// };

const listenersMap = {
  user: new Map<typeof listenerId, Listener>(),
  goods: new Map<typeof listenerId, Listener>(),
  goodsWithNum: new Map<typeof listenerId, Listener>(),
  order: new Map<typeof listenerId, Listener>(),
  card: new Map<typeof listenerId, Listener>(),
  payAuthCode: new Map<typeof listenerId, Listener>(),
  // 全部类型 (用于处理监听到任何扫描的情况)
  all: new Map<typeof listenerId, Listener>(),

  // 用于测试, 响应未解析 code
  test: new Map<typeof listenerId, Listener>(),

  // 以下非扫码触发, `{action标记}{领域}{行为}`，可使用以下正则获取这3个值
  // /^(\w+?)(?=[A-Z])(\w+?)(?=[A-Z])(\w+)$/   /^action(?=[A-Z])(\w+?)(?=[A-Z])(\w+)$/
  actionUserBind: new Map<typeof listenerId, Listener>(),
  actionUserUpdate: new Map<typeof listenerId, Listener>(),
  actionUserUnBind: new Map<typeof listenerId, Listener>(),
  actionUserRecharge: new Map<typeof listenerId, Listener>(),
  actionStartOrder: new Map<typeof listenerId, Listener>(),
  actionCompleteOrder: new Map<typeof listenerId, Listener>(),
};
type ListenerParam = { code: string; type: ScanType; num?: number; payload?: unknown };
export type Listener = {
  (param: ListenerParam): void | true;
};

export type ScanType = keyof typeof listenersMap;

/**
 * 添加监听器
 * @param scanType
 * @param listener
 */
export const addGlobalScanListener = (scanType: ScanType, listener: Listener): string => {
  const listeners = listenersMap[scanType];
  listeners.set(++listenerId, listener);
  return `${scanType}-${listenerId}`;
};

/**
 * 扫码触发
 * @param param0
 */
const trigger = ({ type, ...rest }: ListenerParam) => {
  const listeners = Array.from(new Map(listenersMap[type]).values()).reverse();
  for (const listener of listeners) {
    const stopPropagation = listener({
      type,
      ...rest,
    });
    // 阻止传播
    if (stopPropagation === true) {
      break;
    }
  }
  new Map(listenersMap['all']).forEach(listener => {
    listener({ type, ...rest });
  });
};

/**
 * 触发全局事件 (内部模块提交事件)
 * type 需要以 action 开头
 * @param param0
 */
export const emitGlobalEventWithCode = ({ type, ...rest }: ListenerParam) => {
  if (!type.startsWith(`action`)) {
    throw new Error(`非action的事件只能由扫码枪触发提交`);
  }

  const listeners = new Map(listenersMap[type]);
  listeners.forEach(listener => {
    listener({
      type,
      ...rest,
    });
  });
};
export const emitGlobalEvent = ({ type, ...rest }: Omit<ListenerParam, 'code'>) => {
  return emitGlobalEventWithCode({ type, ...rest, code: '-1' });
};

export const addGlobalEventListener = addGlobalScanListener;

/**
 * 删除监听器
 */
export const removeGlobalScanListener = (...keys: string[]) => {
  keys.forEach(key => {
    const arr = key.split('-');
    if (arr && arr.length === 2) {
      const listeners = listenersMap[arr[0]];
      listeners.delete(+arr[1]);
    } else {
      throw new Error(`移除全局监听器失败，key: ${key}`);
    }
  });
};

/**
 * 键盘监听: ScannerInput
 */
// 接收键位
let receivedKeyDownParams: KeyDownListenerParam[] = [];
export type KeyDownListenerParam = { key: string };
type KeyDownListener = {
  (v: KeyDownListenerParam): void;
};
const keyDownListeners = new Map<
  typeof listenerId,
  { listener: KeyDownListener; target: HTMLElement }
>();

export const addKeyDownListener = ({
  listener,
  target,
}: {
  // 监听函数
  listener: KeyDownListener;
  // 目标元素，用于在触发时进行触发元素比对
  target: HTMLElement;
}): string => {
  keyDownListeners.set(++listenerId, {
    listener,
    target,
  });
  return `${listenerId}`;
};

export const removeKeyDownListener = key => {
  keyDownListeners.delete(+key);
};

const triggerKeyDownListener = (target: HTMLElement) => {
  let param: KeyDownListenerParam;
  const listeners = new Map(keyDownListeners);

  while ((param = receivedKeyDownParams.shift())) {
    listeners.forEach(listener => {
      if (listener.target === target) {
        listener.listener(param);
      }
    });
  }
};

// 接收值
let received = '';
let timer: NodeJS.Timeout = null;

const SCANNER_MIN_RECOGNIZE_LENGTH = 4;
const GOODS_CODE_PATTERN = new RegExp(`^\\d{${SCANNER_MIN_RECOGNIZE_LENGTH},}$`);
const GOODS_WITH_NUM_PATTERN = new RegExp(`^\\d{${SCANNER_MIN_RECOGNIZE_LENGTH},},\\d+$`);

// https://opendocs.alipay.com/open/194/106039
const Alipay_Auth_Code_Pattern = /^[23]\d{15,23}$/;

// https://pay.weixin.qq.com/wiki/doc/api/micropay.php?chapter=5_1
const Wepay_Auth_Code_Pattern = /^1[0-5]\d{16}$/;

const MAX_RECEIVED_HISTORY_COUNT = 15;
const receivedHistory = new Set<string>();
/**有效扫码历史记录 */
const addReceivedHistory = (code: string) => {
  receivedHistory.add(code);
  if (receivedHistory.size >= MAX_RECEIVED_HISTORY_COUNT) {
    receivedHistory.delete(receivedHistory.values().next().value);
  }
};

export const onReceiveCode = (window.__SEND_QR_CODE = code => {
  addReceivedHistory(code);

  trigger({ code, type: 'test' });

  const ownedQrMatches = code.match(/^(writeOff|id):(\w+)\/(\S+)/);
  let goodsListWithNumMatches;

  if (ownedQrMatches && ownedQrMatches.length === 4) {
    const what = ownedQrMatches[1];
    const domain = ownedQrMatches[2];
    const code = ownedQrMatches[3];

    switch (what) {
      case 'writeOff':
        if (domain === 'coupon') {
        }
        break;
      case 'id':
        switch (domain) {
          case 'user':
            trigger({ type: 'user', code });
            return true;
          case 'card':
            trigger({ type: 'card', code });
            return true;
          case 'order':
            trigger({ type: 'order', code });
            return true;
        }
        break;
      default:
      // message.info({ content: `未识别自有二维码: ${what}-${domain}-${param}` });
    }
  } else if (Wepay_Auth_Code_Pattern.test(code) || Alipay_Auth_Code_Pattern.test(code)) {
    trigger({ type: 'payAuthCode', code });
    return true;
  } else if (
    (goodsListWithNumMatches = code.match(GOODS_WITH_NUM_PATTERN)) &&
    goodsListWithNumMatches.length
  ) {
    const arr = goodsListWithNumMatches[0].split(',');
    if (arr && arr.length === 2) {
      trigger({
        type: 'goodsWithNum',
        code: arr[0],
        num: +arr[1],
      });
      return true;
    }
  } else if (GOODS_CODE_PATTERN.test(code)) {
    trigger({ type: 'goods', code });
    return true;
  }
});

const keyPressHandler = e => {
  const tagName = e.target.tagName.toLowerCase();
  if ((tagName === 'input' || tagName === 'textarea') && !e.target.readOnly) {
    return;
  }
  // e.preventDefault();

  const key = e.key.toLowerCase();
  if (key === 'escape') {
    return;
  }

  // 针对 ScannerInput 的处理
  if (e.target.getAttribute('aria-valuetext') === 'scanner') {
    e.preventDefault();
    e.stopPropagation();
    receivedKeyDownParams.push({
      key: e.key,
    });
  }

  if (e.key === 'Enter') {
    if (received.length >= SCANNER_MIN_RECOGNIZE_LENGTH) {
      e.preventDefault();
      e.stopPropagation();
      onReceiveCode(received);
      received = '';
      keyDownListeners.clear();
    } else {
      triggerKeyDownListener(e.target);
    }
  } else {
    clearTimeout(timer);

    // 扫码枪会输入 `shift` 等辅助按键
    if (e.key.length === 1) {
      received = received + e.key;
    }

    // 重置输入值
    timer = setTimeout(() => {
      triggerKeyDownListener(e.target);
      received = '';
    }, +Scanner_Delay);
  }
};

document.addEventListener('keydown', keyPressHandler, true);
